'use client';

import React, { useEffect, useRef, useState } from 'react';

const STEPS = [
  {
    id: 'ask',
    label: 'ASK',
    headline: 'Understand what\'s happening.',
    body: 'Ask anything about your business in plain language. VINA understands intent, not just keywords.',
    color: '#1F4D3F',
    num: '01',
  },
  {
    id: 'analyze',
    label: 'ANALYZE',
    headline: 'Find patterns and opportunities.',
    body: 'VINA connects data across sales, inventory, finance, and customers to surface what matters.',
    color: '#2D6A5A',
    num: '02',
  },
  {
    id: 'act',
    label: 'ACT',
    headline: 'Know what needs attention.',
    body: 'Clear recommendations. Actionable next steps. Decisions you can make with confidence.',
    color: '#3A7D6B',
    num: '03',
  },
];

export default function AnswersClarity() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #FBFAF6 0%, #F6F5F0 100%)' }}
      aria-label="How VINA works"
    >
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] opacity-15"
          style={{ background: 'radial-gradient(ellipse, rgba(31,77,63,0.08) 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
      </div>
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'clamp(2rem, 4.5vw, 3.2rem)',
              letterSpacing: '-0.03em',
              color: '#0D2620',
              lineHeight: 1.15,
              marginBottom: '16px',
            }}
          >
            Answers are useful.
            <br />
            <span style={{ color: '#1F4D3F' }}>Clarity is better.</span>
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '1.05rem',
              color: '#3D5550',
              maxWidth: '440px',
              margin: '0 auto',
              lineHeight: 1.65,
            }}
          >
            VINA doesn&apos;t just answer. It helps you understand and act.
          </p>
        </div>

        {/* Steps — horizontal on desktop, vertical on mobile */}
        <div className="grid md:grid-cols-3 gap-5 relative">
          {/* Connector line on desktop */}
          <div
            className="hidden md:block absolute top-10 left-[calc(16.67%+20px)] right-[calc(16.67%+20px)] h-px"
            style={{ background: 'rgba(211,223,219,0.8)', zIndex: 0 }}
            aria-hidden="true"
          />

          {STEPS?.map((step, i) => (
            <div
              key={step?.id}
              className="relative flex flex-col items-center text-center p-8 rounded-3xl"
              style={{
                background: 'rgba(255,255,255,0.82)',
                border: '1px solid rgba(211,223,219,0.7)',
                boxShadow: i === 1 ? '0 16px 48px rgba(31,77,63,0.08)' : 'none',
                borderColor: i === 1 ? 'rgba(31,77,63,0.15)' : 'rgba(211,223,219,0.7)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.55s ease ${i * 120}ms, transform 0.55s cubic-bezier(0.22,1,0.36,1) ${i * 120}ms`,
                zIndex: 1,
              }}
            >
              {/* Step number circle */}
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-5 flex-shrink-0"
                style={{ background: `${step?.color}12`, border: `1px solid ${step?.color}25` }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: step?.color,
                    letterSpacing: '0.04em',
                  }}
                >
                  {step?.num}
                </span>
              </div>

              {/* Step label */}
              <p
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  color: step?.color,
                  textTransform: 'uppercase',
                  marginBottom: '10px',
                }}
              >
                {step?.label}
              </p>

              {/* Headline */}
              <h3
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: '16px',
                  color: '#0D2620',
                  letterSpacing: '-0.02em',
                  marginBottom: '10px',
                  lineHeight: 1.4,
                }}
              >
                {step?.headline}
              </h3>

              {/* Body */}
              <p style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', color: '#4F665F', lineHeight: 1.7 }}>
                {step?.body}
              </p>

              {/* Arrow between steps on mobile */}
              {i < STEPS?.length - 1 && (
                <div className="md:hidden mt-4 flex justify-center" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 4v12M5 11l5 5 5-5" stroke="#9AADA7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
